import {
  CalendarClock,
  CheckCircle2,
  ClipboardCheck,
  GraduationCap,
  Scale,
} from "lucide-react";
import type { ReactNode } from "react";
import RoundedCard from "@cle/components/RoundedCard";
import {
  ReportStatus,
  type ReportingPeriod,
} from "@cle/models/reportingPeriod";
import { formatDanishLongDate } from "@/util/date";
import ReportedDocumentsTable from "./components/ReportedDocumentsTable";
import ReportPeriodStatusBadge from "./components/ReportPeriodStatusBadge";
import { formatLessons } from "@cle/utils/lessonLimits";
import { REQUIRED_PERIOD_LESSONS as REQUIRED_LESSONS } from "@cle/constants/lessonRequirements";

const MISSING_VALUE = "-";

type ReportedIndberetningDetailViewProps = {
  reportingPeriod: ReportingPeriod;
};

type ReportStatusPresentation = {
  className: string;
  label: string;
  title: string;
};

type ReportedOverviewCategory = {
  key: string;
  label: string;
  submittedLessons: number;
  transferredToNext: number;
};

type ReportedMetricVariant = "default" | "danger";

const REPORT_STATUS_PRESENTATION: Record<
  ReportStatus,
  ReportStatusPresentation
> = {
  [ReportStatus.HANDLED]: {
    className: "cle-report-detail__reported-status--handled",
    label: "Behandlet",
    title: "Indberetningen er behandlet",
  },
  [ReportStatus.EXPIRED]: {
    className: "cle-report-detail__reported-status--warning",
    label: "Udløbet",
    title: "Indberetningen er registreret som udløbet",
  },
  [ReportStatus.INSUFFICIENT]: {
    className: "cle-report-detail__reported-status--warning",
    label: "Mangler lektioner",
    title: "Indberetningen mangler lektioner",
  },
  [ReportStatus.COMPLETED]: {
    className: "cle-report-detail__reported-status--completed",
    label: "Indberettet",
    title: "Indberetningen er modtaget",
  },
  [ReportStatus.TOO_LATE]: {
    className: "cle-report-detail__reported-status--completed",
    label: "Indberettet",
    title: "Indberetningen er modtaget efter fristen",
  },
};

const REPORTED_OVERVIEW_CATEGORIES = [
  { key: "kursus", label: "Kursusdeltagelse" },
  { key: "undervisning", label: "Undervisning" },
  { key: "forfatter", label: "Forfatterskab" },
  { key: "reduktion", label: "Reduktion" },
  { key: "advokatvagt", label: "Advokatvagt/retshjælp" },
  { key: "dispensation", label: "Dispensation" },
] as const;

const isSubmittedAfterDeadline = (
  submittedAt: string | Date | null | undefined,
  deadline: string | Date | null | undefined,
): boolean => {
  if (!submittedAt || !deadline) {
    return false;
  }

  return new Date(submittedAt).getTime() > new Date(deadline).getTime();
};

const getReportStatusPresentation = ({
  deadline,
  status,
  submittedAt,
}: {
  deadline: string | null;
  status: ReportStatus | undefined;
  submittedAt: string | Date | null | undefined;
}): ReportStatusPresentation => {
  if (
    status === ReportStatus.TOO_LATE ||
    isSubmittedAfterDeadline(submittedAt, deadline)
  ) {
    return REPORT_STATUS_PRESENTATION[ReportStatus.TOO_LATE];
  }

  if (!status) {
    return {
      className: "cle-report-detail__reported-status--completed",
      label: "Indberettet",
      title: "Indberetningen er modtaget",
    };
  }

  return REPORT_STATUS_PRESENTATION[status];
};

const formatPeriodRange = (reportingPeriod: ReportingPeriod) => {
  return `${formatDanishLongDate(
    reportingPeriod.periodFromDate,
    MISSING_VALUE,
  )} - ${formatDanishLongDate(
    reportingPeriod.periodToDateMinusOneDay,
    MISSING_VALUE,
  )}`;
};

const createReportedOverviewCategories = (
  reportingPeriod: ReportingPeriod,
): ReportedOverviewCategory[] =>
  REPORTED_OVERVIEW_CATEGORIES.map(({ key, label }) => {
    const submittedLessons = reportingPeriod.reportedLessons?.[key] ?? 0;
    const transferredToNext =
      reportingPeriod.transferredToNextLessons?.[key] ?? 0;

    return {
      key,
      label,
      submittedLessons,
      transferredToNext,
    };
  });

const ReportedMetric = ({
  icon,
  label,
  value,
  variant = "default",
}: {
  icon: ReactNode;
  label: string;
  value: string | number;
  variant?: ReportedMetricVariant;
}) => (
  <div
    className={[
      "cle-report-detail__reported-metric",
      variant === "danger"
        ? "cle-report-detail__reported-metric--danger"
        : undefined,
    ]
      .filter(Boolean)
      .join(" ")}
  >
    <span className="cle-report-detail__reported-metric-icon">{icon}</span>
    <span>
      <span className="cle-report-detail__reported-metric-label">{label}</span>
      <span className="cle-report-detail__reported-metric-value">{value}</span>
    </span>
  </div>
);

const ReportedIndberetningDetailView = ({
  reportingPeriod,
}: ReportedIndberetningDetailViewProps) => {
  const report = reportingPeriod.report;
  const status = getReportStatusPresentation({
    deadline: reportingPeriod.deadline,
    status: report?.status,
    submittedAt: report?.uploaded_at,
  });
  const periodRange = formatPeriodRange(reportingPeriod);
  const deadline = formatDanishLongDate(
    reportingPeriod.deadline,
    "Ingen frist",
  );
  const submittedAt = formatDanishLongDate(report?.uploaded_at, MISSING_VALUE);
  const transferredLessons = reportingPeriod.transferredToNextPeriod ?? 0;
  const isSubmittedLate =
    report?.status === ReportStatus.TOO_LATE ||
    isSubmittedAfterDeadline(report?.uploaded_at, reportingPeriod.deadline);
  const reportedOverviewCategories =
    createReportedOverviewCategories(reportingPeriod);
  const periodId = reportingPeriod.id;
  const overviewTotals = reportedOverviewCategories.reduce(
    (totals, category) => ({
      submittedLessons: totals.submittedLessons + category.submittedLessons,
      transferredToNext: totals.transferredToNext + category.transferredToNext,
    }),
    {
      submittedLessons: 0,
      transferredToNext: 0,
    },
  );
  const reportedLessons = overviewTotals.submittedLessons;
  const hasInsufficientLessons = reportedLessons < REQUIRED_LESSONS;

  return (
    <div className="cle-report-detail">
      <section className="cle-report-detail__reported-shell">
        <div className="cle-report-detail__reported-hero cle-report-detail__reported-hero-success">
          <div className="cle-report-detail__reported-hero-content">
            <p className="cle-afventende-indberetninger-card__subtitle mb-0">
              Efteruddannelsesperiode
            </p>
            <h1 className="cle-report-detail__reported-title">{periodRange}</h1>

            <p className="cle-report-detail__reported-description">
              Din indberetning for efteruddannelsesperioden er registreret. Her
              kan du se de vigtigste oplysninger om den indsendte indberetning.
            </p>
          </div>
          <ReportPeriodStatusBadge
            className={status.className}
            icon={<CheckCircle2 size={16} aria-hidden strokeWidth={2} />}
            label={status.label}
          />
        </div>

        <RoundedCard className="cle-report-detail__reported-summary">
          <div className="cle-report-detail__reported-summary-header mb-0">
            <div>
              <h2 className="cle-report-detail__reported-period mb-0">
                Information om perioden
              </h2>
              <p className="cle-report-detail__reported-description">
                {periodRange}
              </p>
            </div>
          </div>

          <div className="cle-report-detail__reported-metrics">
            <ReportedMetric
              icon={<CalendarClock size={18} aria-hidden strokeWidth={2} />}
              label="Indberetningsfrist"
              value={deadline}
            />
            <ReportedMetric
              icon={<GraduationCap size={18} aria-hidden strokeWidth={2} />}
              label="Indberettede lektioner"
              value={`${formatLessons(reportedLessons)} / ${formatLessons(
                REQUIRED_LESSONS,
              )}`}
              variant={hasInsufficientLessons ? "danger" : "default"}
            />
            <ReportedMetric
              icon={<ClipboardCheck size={18} aria-hidden strokeWidth={2} />}
              label={isSubmittedLate ? "Indsendt for sent" : "Indsendt"}
              value={submittedAt}
              variant={isSubmittedLate ? "danger" : "default"}
            />
            <ReportedMetric
              icon={<Scale size={18} aria-hidden strokeWidth={2} />}
              label="Overført til næste periode"
              value={`${transferredLessons} lektioner`}
            />
          </div>
        </RoundedCard>

        <RoundedCard
          bodyClassName="p-0"
          header="Overblik over indberetning"
          description="Oversigt over de lektioner og overførsler, der indgår i den indsendte indberetning."
        >
          <div className="table-responsive">
            <table className="table align-middle mb-0 cle-report-detail__overview-table">
              <thead>
                <tr>
                  <th scope="col">Kategori</th>
                  <th scope="col" className="text-end">
                    Indberettet
                  </th>
                  <th scope="col" className="text-end">
                    Overført til næste periode
                  </th>
                </tr>
              </thead>
              <tbody>
                {reportedOverviewCategories.map((category) => (
                  <tr key={category.key}>
                    <th scope="row">{category.label}</th>
                    <td className="text-end">{category.submittedLessons}</td>
                    <td className="text-end">{category.transferredToNext}</td>
                  </tr>
                ))}
                <tr className="cle-report-detail__overview-total-row">
                  <th scope="row">I alt</th>
                  <td className="text-end">
                    {overviewTotals.submittedLessons}
                  </td>
                  <td className="text-end">
                    {overviewTotals.transferredToNext}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </RoundedCard>

        <RoundedCard
          bodyClassName="p-0 pt-2"
          header="Dokumenter brugt i indberetningen"
          description="Dokumenter, der er medtaget i den indsendte indberetning."
        >
          <ReportedDocumentsTable periodId={periodId} />
        </RoundedCard>

        <RoundedCard className="cle-report-detail__reported-timeline">
          <div className="cle-report-detail__reported-timeline-item">
            <span className="cle-report-detail__reported-timeline-icon">
              <CheckCircle2 size={18} aria-hidden strokeWidth={2} />
            </span>
            <div>
              <h2 className="cle-report-detail__reported-section-title">
                Indberetningen er låst til visning
              </h2>
              <p className="text-muted small mb-0">
                Denne side viser en opsummering af den indberetning, der
                allerede er modtaget. Hvis der skal ændres i indberetningen, kan
                Advokatsamfundet kontaktes.
              </p>
            </div>
          </div>
        </RoundedCard>
      </section>
    </div>
  );
};

export default ReportedIndberetningDetailView;
